import Select from "../../components/ui/Select";
import Button from "../../components/ui/Button";
import Pagination from "../../components/ui/Pagination";
import React, { useState, useEffect } from "react";
import "./AuditMgtPage.css";
import {
  AuditLog,
  AuditMeta,
  getMyAuditLogs,
} from "../../services/auditService";
import {
  AUDIT_ACTOR_TYPES,
  AUDIT_ACTIONS,
  AUDIT_RESOURCES,
  AuditFilters,
} from "../../types/audit";
import searchIcon from "../../assets/images/searchicon.png";

const ITEMS_PER_PAGE = 20;

const EMPTY_FILTERS: AuditFilters = {
  search: "",
  actorId: "",
  actorType: "",
  action: [],
  resource: [],
};

function AuditMultiFilter<T extends string>({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: readonly T[];
  selected: T[];
  onChange: (values: T[]) => void;
}) {
  return (
    <details className="audit-multi-filter">
      <summary>
        <span>{label}</span>
        <span className="audit-filter-value">
          {selected.length ? `${selected.length} selected` : "All"}
        </span>
      </summary>
      <div className="audit-filter-options" role="group" aria-label={label}>
        {options.map((value) => (
          <label key={value}>
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...selected, value]
                    : selected.filter((item) => item !== value),
                )
              }
            />
            <span>{value.replace(/_/g, " ")}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

const auditDevice = (log: AuditLog) => {
  const device = log.metadata?.device;
  return (
    [device?.device, device?.os, device?.browser].filter(Boolean).join(" / ") ||
    log.metadata?.userAgent ||
    "—"
  );
};

const auditDate = (log: AuditLog) => {
  const value = log.createdAt;
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const AuditMgtPage: React.FC = () => {
  const [filters, setFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const updateFilter = <K extends keyof AuditFilters>(
    key: K,
    value: AuditFilters[K],
  ) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setCurrentPage(1);
  };
  const hasFilters = !!(
    filters.search ||
    filters.actorId ||
    filters.actorType ||
    filters.action.length ||
    filters.resource.length
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [audits, setAudits] = useState<AuditLog[]>([]);
  const [meta, setMeta] = useState<AuditMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const result = await getMyAuditLogs(
          currentPage,
          filters,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setAudits(result.audits);
        setMeta(result.meta);
      } catch (err) {
        if (controller.signal.aborted) return;
        setAudits([]);
        setMeta(null);
        setError("Unable to load audit logs. Please try again.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [currentPage, filters, retry]);

  const total = meta?.total;
  const limit = meta?.limit ?? ITEMS_PER_PAGE;
  const totalPages =
    meta?.totalPages ??
    (total !== undefined ? Math.max(1, Math.ceil(total / limit)) : undefined);
  const hasNext =
    totalPages !== undefined
      ? currentPage < totalPages
      : audits.length === ITEMS_PER_PAGE;

  return (
    <div className="audit-mgt-page">
      <header className="page-header audit-search-filters">
        <label>
          Actor ID
          <input
            value={filters.actorId}
            placeholder="Admin or user ID"
            onChange={(e) => updateFilter("actorId", e.target.value)}
          />
        </label>
        <label>
          Actor type
          <Select
            value={filters.actorType}
            onChange={(e) =>
              updateFilter(
                "actorType",
                e.target.value as AuditFilters["actorType"],
              )
            }
          >
            <option value="">All actor types</option>
            {AUDIT_ACTOR_TYPES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </label>

        <div className="audit-search-box">
          <img src={searchIcon} alt="" />
          <input
            type="text"
            aria-label="Search audit logs"
            placeholder="Search audit logs…"
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
          />
          {filters.search && (
            <button
              className="clear-btn"
              aria-label="Clear search"
              onClick={() => updateFilter("search", "")}
            >
              ×
            </button>
          )}
        </div>
      </header>

      <div className="audit-filters">
        <AuditMultiFilter
          label="Actions"
          options={AUDIT_ACTIONS}
          selected={filters.action}
          onChange={(values) => updateFilter("action", values)}
        />
        <AuditMultiFilter
          label="Resources"
          options={AUDIT_RESOURCES}
          selected={filters.resource}
          onChange={(values) => updateFilter("resource", values)}
        />
        <Button
          type="button"
          disabled={!hasFilters}
          onClick={() => {
            setFilters(EMPTY_FILTERS);
            setCurrentPage(1);
          }}
        >
          Clear filters
        </Button>
      </div>

      {loading ? (
        <div role="status">Loading audit logs...</div>
      ) : error ? (
        <div role="alert">
          {error}{" "}
          <Button type="button" onClick={() => setRetry((value) => value + 1)}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Action taken</th>
                <th>Date</th>
                <th>IP Address</th>
                <th>Device / browser</th>
                <th>Page / resource</th>
              </tr>
            </thead>
            <tbody>
              {audits.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    {hasFilters
                      ? "No audit logs match your filters."
                      : "No audit logs yet."}
                  </td>
                </tr>
              ) : (
                audits.map((log) => (
                  <tr key={log.id}>
                    <td data-label="Name">{log.actorName || "—"}</td>
                    <td data-label="Email">{log.actorEmail || "—"}</td>
                    <td data-label="Action taken">{log.action}</td>
                    <td data-label="Date">{auditDate(log)}</td>
                    <td data-label="IP Address">{log.metadata?.ip || "—"}</td>
                    <td data-label="Device / browser">{auditDevice(log)}</td>
                    <td data-label="Page / resource">
                      {log.metadata?.path ||
                        [log.resource, log.resourceId]
                          .filter(Boolean)
                          .join(" / ")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={total} hasNext={hasNext} onPageChange={setCurrentPage} loading={loading} disabled={!!error} />
    </div>
  );
};

export default AuditMgtPage;
